import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useConfig } from 'msp-integrations'
import RadioButton from '@atoms/RadioButton/RadioButton'
import * as T from './VariantSelector.types'
import GiftCertBalanceModal from '@organisms/GiftCertBalanceModal/GiftCertBalanceModal'
import CustomLink from '@atoms/CustomLink/CustomLink'
import { twMerge } from 'tailwind-merge'

const formatVariantLabel = (label: string): string =>
  (label || '').replace(/-/g, ' ')

const getVariationKey = (variationValues: T.VariationTypeProps) => {
  let variationKey = ''
  Object.keys(variationValues).forEach((key: string, index: number) => {
    if (index === 0) {
      variationKey += variationValues[key]
    } else {
      variationKey += `-${variationValues[key]}`
    }
  })
  return variationKey
}

const VariantSelector = ({
  variantSelected,
  variants,
  variationAttributes,
  onVariantChange,
  isDarkMode = false,
  isGiftCert = false,
  isPDP = false
}: {
  variantSelected: T.VariantProps
  variants: T.VariantProps[]
  variationAttributes: T.VariantAttributeProps[]
  onVariantChange: (productId: string, giftCertIsVirtual?: boolean) => void
  isDarkMode?: boolean
  isGiftCert?: boolean
  isPDP?: boolean
}) => {
  const {
    theme: { font }
  } = useConfig()
  const { formatMessage } = useIntl()
  const [showGiftCertBalanceModal, setShowGiftCertBalanceModal] =
    useState(false)
  const [currentVariationValues, setCurrentVariationValues] = useState(
    variantSelected?.variationValues || {}
  )
  const [giftCertIsVirtual, setGiftCertIsVirtual] = useState(true)

  const variantsByPossibleSelections = {} as any
  variants.forEach((variant) => {
    variantsByPossibleSelections[getVariationKey(variant.variationValues)] =
      variant
  })

  const onVariationChange = (id: string, value: string) => {
    currentVariationValues[id] = value
    setCurrentVariationValues(currentVariationValues)
    const variationKey = getVariationKey(currentVariationValues)
    if (variantsByPossibleSelections[variationKey]) {
      onVariantChange(
        variantsByPossibleSelections[variationKey].productId,
        giftCertIsVirtual
      )
    }
  }

  const onGiftCertTypeChange = (isVirtual: boolean) => {
    setGiftCertIsVirtual(isVirtual)
    const variationKey = getVariationKey(currentVariationValues)
    if (variantsByPossibleSelections[variationKey]) {
      onVariantChange(
        variantsByPossibleSelections[variationKey].productId,
        isVirtual
      )
    }
  }

  return (
    <div className='flex flex-col space-y-2 border-y-[1px] border-solid border-[#e4e1df] py-[30px]'>
      {isGiftCert && (
        <div className='mb-3'>
          <CustomLink onClick={() => setShowGiftCertBalanceModal(true)}>
            {formatMessage({
              id: 'msp.variant.selector.gift_cert.balance.link',
              defaultMessage: 'Check balance of your current Gift Card'
            })}
          </CustomLink>
          <GiftCertBalanceModal
            showModal={showGiftCertBalanceModal}
            setShowModal={setShowGiftCertBalanceModal}
          />
        </div>
      )}
      <div
        className={twMerge(
          'flex',
          isGiftCert ? 'flex-col-reverse space-y-7 space-y-reverse' : 'flex-col'
        )}
      >
        {variationAttributes.map((variationAttribute) => {
          let { id, name, values = [] } = variationAttribute
          return (
            <div key={`variant-${id}`}>
              <p
                className={`${
                  font.regular
                } text-15 font-medium transition-colors duration-500 ${
                  isDarkMode ? 'text-white' : 'text-black'
                } ${isGiftCert && 'mb-2'}`}
              >
                {`${name}:`}
              </p>
              <div className='flex flex-wrap items-center gap-4'>
                {values.map((variationAttributeValue) => {
                  let { name, value, image } = variationAttributeValue

                  if (image) {
                    return (
                      <div
                        key={`variant-value-${value}`}
                        className={`cursor-pointer border-[3px] ${
                          currentVariationValues[id] === value
                            ? 'border-black'
                            : 'border-white'
                        }`}
                        onClick={() => onVariationChange(id, value)}
                      >
                        <picture>
                          <source
                            media='(min-width: 768px)'
                            srcSet={`${image?.sm || ''} 1x, ${image?.md || ''}`}
                          />
                          <source
                            media='(min-width: 0px)'
                            srcSet={`${image?.xl || ''} 1x`}
                          />
                          <img src={image?.sm || ''} alt={name} />
                        </picture>
                      </div>
                    )
                  }

                  return (
                    <RadioButton
                      key={`variant-value-${value}`}
                      type={'radio'}
                      value={value}
                      checkedValue={
                        variantSelected?.variationValues[id] ||
                        currentVariationValues[id]
                      }
                      label={formatVariantLabel(name)}
                      className={`${font.regular} capitalize ${
                        isDarkMode ? 'text-white' : 'text-black'
                      }`}
                      onChange={() => onVariationChange(id, value)}
                      isValueVariant={id === 'valueGiftcard'}
                      registered // TODO: This 'registered' is neccesary for the correct behaviour, the RadioButton component is too AD HOC to the Registration form, should be refactored
                    />
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
      {isPDP && isGiftCert && (
        <div className='pt-5'>
          <div>
            <RadioButton
              type={'radio'}
              value={'no'}
              checkedValue={giftCertIsVirtual ? 'yes' : 'no'}
              label={formatMessage({
                id: 'msp.variant.selector.giftCert.post.label',
                defaultMessage:
                  'Gift card by post (delivery time approx. 2 days)'
              })}
              className={`${font.regular} capitalize ${
                isDarkMode ? 'text-white' : 'text-black'
              }`}
              onChange={() => onGiftCertTypeChange(false)}
              registered // TODO: This 'registered' is neccesary for the correct behaviour, the RadioButton component is too AD HOC to the Registration form, should be refactored
            />
          </div>
          <div>
            <RadioButton
              type={'radio'}
              value={'yes'}
              checkedValue={giftCertIsVirtual ? 'yes' : 'no'}
              label={formatMessage({
                id: 'msp.variant.selector.giftCert.virtual.label',
                defaultMessage: 'Gift card by email (to print out yourself)'
              })}
              className={`${font.regular} capitalize ${
                isDarkMode ? 'text-white' : 'text-black'
              }`}
              onChange={() => onGiftCertTypeChange(true)}
              registered // TODO: This 'registered' is neccesary for the correct behaviour, the RadioButton component is too AD HOC to the Registration form, should be refactored
            />
          </div>
          <div className='pl-9'>
            <small>
              {formatMessage({
                id: 'msp.variant.selector.giftCert.virtual.info.label',
                defaultMessage:
                  'If you are really in a hurry to get a gift card, they can also be purchased digitally. We will send the gift card to you via email straight after we’ve received your order. Please note: The “gift card by email” option cannot be purchased together with other products – it must be purchased in a separate order.'
              })}
            </small>
          </div>
        </div>
      )}
    </div>
  )
}

export default VariantSelector
